// **قشرة الموبايل** (قرار المالك 21/09/2026: «شكل الزراير كأنها مدوّنة — عاوزه Premium
// بجد، high end، وكأنه تطبيق احترافي»).
//
// قبلها كانت قائمةً من صفوفٍ عريضةٍ ملوّنة فوق بعض: تُقرأ كمقالٍ لا كتطبيق. والفرق بين
// الاثنين ليس زخرفةً تُضاف، بل **بنيةٌ تُشبه ما اعتادته اليد على الهاتف**:
//
//  1. **شريطٌ سفليّ ثابت** بأربع وجهاتٍ ورمزٍ لكلٍّ منها و«المزيد» — الإبهام يصله بلا
//     مدٍّ، والمفتوح منها يلبس قرصًا بلون عائلته لا مجرّد لونٍ في النصّ.
//  2. **ترويسةٌ متدرّجة** فيها اسم الشاشة وفوقه اسم عائلتها بخطٍّ خفيف — سطران يقولان
//     «أين أنت» في لمحة، بدل شريطٍ مصمتٍ باسمٍ واحد.
//  3. **لوحٌ يُسحب من أسفل** فيه كل الشاشات **شبكةَ بطاقاتٍ برموز** لا صفوفًا، وفيه
//     أدوات الترويسة في بطاقةٍ مستقلّة، والمجموعات تُطوى وتُفتح.
//
// **ومقاسات هذه القشرة بالبكسل لا بالـrem** عن قصد: جذر الصفحة مصغَّر على الموبايل
// (انظر index.css)، فلو تبعته لانكمش الشريط السفلي معه — وشريط التطبيق لا ينكمش.
//
// **والويب لا يُمسّ حرفًا**: هذا كلّه داخل `lg:hidden`، وشريطُ التبويبات القديم داخل
// `hidden lg:block`. فهما شكلان لا يلتقيان، والمتصفّح يعرض واحدًا بحسب عرض الشاشة.
import { useEffect, useMemo, useState, type CSSProperties, type ReactNode } from 'react';
import { navIcon } from './navIcons.tsx';
import { useTheme } from '../theme/theme.ts';
import { greetAr } from '../lib/greeting.ts';
import { displayPhone } from '../lib/format.ts';
import type { Me } from '../lib/api.ts';

export interface MobileTab { id: string; label: string; badge?: number; group?: 'tools' | 'settings' }

/**
 * ألوان اللوجو — **درجتان لكل عائلة**، لأن اللون الواحد لا يخدم أرضيتين:
 *   · `on` غامقةٌ تُقرأ على الأبيض — للوضع الفاتح.
 *   · `onDark` فاتحةٌ تُقرأ على الأسود — للوضع الداكن. ولولاها لصار نصُّ الرمل بنّيًّا
 *     على أسودَ فلا يُرى.
 * و`soft`/`softDark` أرضيةُ القرص تحت الرمز: تلميحُ لونٍ لا لونٌ مصمت.
 */
const TONE = {
  sea:    { on: '#0b7f88', onDark: '#4fd0da', soft: '#e3f6f7', softDark: 'rgb(79 208 218 / .15)' },   // تركواز البحر
  brand:  { on: '#1b4691', onDark: '#7fa9f2', soft: '#eaf1fe', softDark: 'rgb(127 169 242 / .15)' },  // أزرق النظام
  sand:   { on: '#8a5d12', onDark: '#e0b352', soft: '#fbf0d9', softDark: 'rgb(224 179 82 / .15)' },   // ذهبيّ الرمل
  sunset: { on: '#9e4a18', onDark: '#f0945c', soft: '#fdeade', softDark: 'rgb(240 148 92 / .15)' },   // برتقاليّ الغروب
  palm:   { on: '#2a6742', onDark: '#6cc48c', soft: '#e4f3e9', softDark: 'rgb(108 196 140 / .15)' },  // أخضر النخل
  slate:  { on: '#3b475a', onDark: '#a3b1c6', soft: '#f1f5f9', softDark: 'rgb(163 177 198 / .16)' },
} as const;
type Family = keyof typeof TONE;

/**
 * **ترويسةٌ واحدة لكل الشاشات** — وكان تدرّجُها بلون العائلة نفسها.
 *
 * ولونُ الرمل أو الغروب جميلٌ نقطةً صغيرة، أمّا جدارًا بعرض الشاشة فيصير طينًا
 * («الألوان سيئة جدًّا» — قرار المالك 22/09/2026). فالجدار حبرٌ أزرقُ عميقٌ ثابت لا
 * يتبدّل، **والعائلة تُعرف بخطٍّ رفيعٍ أسفل الترويسة** وبرموز الشريط السفلي: لونٌ
 * يُشير ولا يصرخ. وهو حبرٌ واحد في الوضعين فلا يحتاج مقابلًا داكنًا.
 */
const HEADER = 'linear-gradient(120deg, #10394d 0%, #0b2739 58%, #081d2b 100%)';

/** عائلةُ كلِّ شاشة — ومنها لونُها واسمُها فوق العنوان. وما لم يُذكر فأزرق النظام. */
const FAMILY: Record<string, Family> = {
  home: 'sea', crm: 'sea', bookings: 'sea', declarations: 'sea', upcoming: 'sea', 'admin-crm': 'sea', chat: 'sea', 'team-chat': 'sea', guide: 'sea', airports: 'sea',
  'admin-dashboard': 'brand', 'admin-analytics': 'brand', 'admin-payments': 'brand',
  treasury: 'sand', 'admin-settlements': 'sand', commission: 'sand', 'google-rating': 'sand',
  'my-payslip': 'sand', 'my-partner-account': 'sand',
  attendance: 'palm', 'admin-attendance': 'palm', 'admin-employees': 'palm',
  'my-rank': 'palm', 'evaluate-me': 'palm',
  quote: 'sunset', pricing: 'sunset', installment: 'sunset', clock: 'sunset', fx: 'sunset',
  'hotel-voucher': 'sunset', tafqeet: 'sunset', hijri: 'sunset',
  'my-notes': 'slate', account: 'slate', 'account-password': 'slate', 'account-lock': 'slate',
  'admin-audit': 'slate', 'admin-backups': 'slate',
};
/** «الرئيسية» لا عائلةَ لها تُذكر فوق العنوان — هي البيت لا بابًا فيه. */
const CAPTION: Record<Family, string> = {
  sea: 'الشغل اليومي', brand: 'اللوحة والتقارير', sand: 'الفلوس',
  palm: 'الحضور والتقييم', sunset: 'أدوات ومراجع', slate: 'حسابك وإعداداته',
};
const familyOf = (id: string): Family => FAMILY[id] ?? 'brand';

/** لونا العائلة كما يُرسمان في الوضع الجاري: `fg` للنصّ والرمز، و`chip` لأرضية القرص. */
function paint(id: string, dark: boolean): { fg: string; chip: string } {
  const t = TONE[familyOf(id)];
  return dark ? { fg: t.onDark, chip: t.softDark } : { fg: t.on, chip: t.soft };
}
/** ألوانُ البطاقة غير المختارة — رماديٌّ يتبع الوضع، لا لونَ عائلةٍ باهتًا. */
const IDLE_LINE = (dark: boolean) => (dark ? '#1f2a3b' : '#e2e8f0');
const IDLE_FACE = (dark: boolean) => (dark ? 'transparent' : '#fff');
const IDLE_TEXT = (dark: boolean) => (dark ? '#c9d0db' : '#1e293b');

/** اسمٌ قصير للشريط السفلي: خانةٌ بخُمس الشاشة لا تسع «الحضور والانصراف». */
const SHORT: Record<string, string> = {
  home: 'الرئيسية', crm: 'المتابعات', bookings: 'الحجوزات', declarations: 'الإقرار', upcoming: 'القادمة', 'admin-crm': 'النشاط',
  'admin-dashboard': 'اللوحة', 'admin-analytics': 'التحليل', 'admin-payments': 'التحصيل',
  'admin-settlements': 'المالية', 'admin-attendance': 'الحضور', 'admin-employees': 'الموظفين',
  treasury: 'الخزنة', commission: 'العمولات', 'my-payslip': 'مرتّبي',
  'my-partner-account': 'حساباتي', attendance: 'الحضور', 'my-rank': 'ترتيبي',
  'evaluate-me': 'قيّمني', 'google-rating': 'التقييم', 'my-notes': 'ملاحظاتي', guide: 'الدليل', airports: 'المطارات', 'team-chat': 'الفريق',
};
/** الرمز التعبيري في أوّل الاسم زائدٌ متى صار للشاشة رمزٌ مرسوم (مثل «🔒 الخزنة»). */
const clean = (s: string) => s.replace(/^\p{Extended_Pictographic}️?\s*/u, '');
const short = (t: MobileTab) => SHORT[t.id] ?? clean(t.label).split(' ')[0];

/**
 * **القشرة تُبنى حين تكون الشاشة شاشةَ موبايل، لا تُرسم ثم تُخفى بالـCSS.**
 *
 * المخفيُّ بالـCSS يبقى موجودًا في الصفحة: زرّان باسم «الحجوزات»، وعنوانان للشاشة —
 * فيلتبس الأمر على كل من يقرأ الصفحة لا بعينَي متصفّحٍ حمَّل الأنماط: قارئُ الشاشة،
 * والاختبار. والاستعلام يُسأل مرّةً عند البناء فلا ومضة، ثم يُتابَع فيستجيب للتدوير.
 */
const NARROW = '(max-width: 1023px)';
export function useNarrow(): boolean {
  const ask = () => typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    && window.matchMedia(NARROW).matches;
  const [narrow, setNarrow] = useState(ask);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia(NARROW);
    const on = () => setNarrow(mq.matches);
    on();
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  return narrow;
}

const GROUPS = [
  { key: 'tools' as const, ar: 'أدوات هامة' },
  { key: 'settings' as const, ar: 'إعدادات الحساب' },
];

export function MobileNav({ tabs, active, onPick, quickTap, search, me, onSignOut, kpi, bell, children }: {
  tabs: readonly MobileTab[];
  /** «📊 KPI» في الترويسة بجانب «تسجيل الخروج» (المالك 04/10/2026: «ال kpi من الموبيل مبتظهرش ومش عارف افتحها») */
  kpi?: ReactNode;
  /**
   * **الجرس في الترويسة دائمًا** (المراجعة الشاملة 06/10/2026، المالك: «نفذ كدا تمام موافق»): كان في اللوح خلف «المزيد»
   * فلا يُركَّب إلّا والقائمة مفتوحة — لا صوت ولا نافذة ولا عدّاد طول ما هي مقفولة. هنا يُركَّب مع الصفحة كالكمبيوتر.
   */
  bell?: ReactNode;
  active: string;
  onPick: (id: string) => void;
  /**
   * **بصمة الحضور والانصراف** — في الترويسة لا في اللوح (قرار المالك 21/09/2026:
   * «تبقى ظاهرة وصغيرة عادي»): من يبصم يبصم أوّل ما يفتح، فلا يُخبَّأ خلف قائمة.
   */
  quickTap?: ReactNode;
  /** **البحث** — بجانب البصمة في الترويسة (قرار المالك): أكثرُ ما يُستعمل، فأقربُ ما يُوضع. */
  search?: ReactNode;
  /**
   * صاحبُ الجلسة — لتحيّته باسمه ولعرض هاتفه المسجَّل (قرار المالك 22/09/2026: «عاوز
   * من الموبايل يقوله صباح الخير، إزيك يا أحمد شوقي، وكمان فوق خالص يكتب اسمه ورقم
   * تليفونه المسجَّل على البرنامج دايمًا فوق خالص»).
   */
  me?: Me;
  /** **الخروج** — «بره خالص قدامهم» (قرار المالك): زرٌّ ظاهرٌ دائمًا لا بندٌ في قائمة. */
  onSignOut?: () => void;
  /** بقيّة أدوات الترويسة (البحث · الجرس · الإعلان…) — في اللوح */
  children?: ReactNode;
}) {
  const narrow = useNarrow();
  // الوضع يُقرأ هنا مرّةً ويُمرَّر: ألوان هذه القشرة أنماطٌ محسوبة (style) لا أصنافًا،
  // فلا تصلها ورقةُ الوضع الداكن المولَّدة — فتُحسب بالوضع صراحةً.
  const { dark } = useTheme();
  const [open, setOpen] = useState(false);
  const [openGroup, setOpenGroup] = useState<string | null>(null);
  const current = tabs.find(t => t.id === active);
  const accent = paint(active, dark).fg;

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => { document.body.style.overflow = prev; window.removeEventListener('keydown', onKey); };
  }, [open]);

  const pending = tabs.reduce((n, t) => n + (t.badge ?? 0), 0);
  const plain = useMemo(() => tabs.filter(t => !t.group), [tabs]);

  /**
   * وجهاتُ الشريط السفلي: الأربعُ الأولى — وهي مرتّبةٌ بالأهمّية أصلًا في `App` — إلّا أن
   * تكون الشاشةُ المفتوحة خارجها فتحلّ محلّ الرابعة. فلا يُفتح شيءٌ بلا أثرٍ في الشريط.
   */
  const primary = useMemo(() => {
    const first = plain.slice(0, 4);
    if (first.some(t => t.id === active)) return first;
    const cur = plain.find(t => t.id === active);
    return cur ? [...first.slice(0, 3), cur] : first;
  }, [plain, active]);

  const pick = (id: string) => { onPick(id); setOpen(false); };

  // بعد كل الخطّافات لا قبلها: ترتيبُها لا يتغيّر بين رسمٍ وآخر
  if (!narrow) return null;

  return (
    <div className="lg:hidden">
      {/* الترويسة: حبرٌ ثابت، والعائلة خطٌّ رفيعٌ أسفلها — يُعرف المكان بالعين لا بجدارٍ ملوّن */}
      <header className="sticky top-0 z-40 shadow-[0_1px_12px_-2px_rgba(2,6,23,.45)]"
        style={{ backgroundImage: HEADER }}>
        {/**
          * **سطرُ صاحب الجلسة فوق كل شيء**: تحيّةُ الساعة باسمه، وهاتفُه المسجَّل.
          * والهاتفُ من حسابه لا من ذاكرته — فمن سُئل عن رقمه المسجَّل وجده، ومن
          * وجده خطأً عرف أن يصحّحه. و«—» حين لا رقمَ مسجَّل: لا رقمَ يُخترع.
          */}
        {me && (
          <div className="flex items-center justify-between gap-2 px-3.5 pb-1 text-white/85"
            style={{ paddingTop: 'calc(8px + env(safe-area-inset-top))' }}>
            <span className="min-w-0 truncate text-[12px] font-semibold">
              {greetAr(me.fullNameAr)}
            </span>
            <span dir="ltr" className="shrink-0 text-[11px] tabular-nums text-white/70">
              {displayPhone(me.phone)}
            </span>
          </div>
        )}
        <div className="flex items-center gap-3 px-3.5 pb-2.5"
          style={me ? { paddingTop: 0 } : { paddingTop: 'calc(10px + env(safe-area-inset-top))' }}>
          {/* دائرة الشعار وحدها بجودتها الكاملة (قرار المالك 03/10/2026: «الأيقونة… من جوّه محتاجة جودة عالية») —
              اللوحة الأصلية بهامشها الشفّاف كانت تُصغّر الرسم في هذا المربّع الصغير فيبدو باهتًا */}
          <img src="/assets/icontravel-mark.png" alt="" aria-hidden="true" width={36} height={36}
            className="h-9 w-9 shrink-0 rounded-full shadow-sm" />
          <div className="min-w-0 flex-1">
            <div className="text-[10.5px] font-semibold tracking-[.14em] text-white/60">
              {CAPTION[familyOf(active)]}
            </div>
            <div className="truncate text-[17px] font-bold leading-tight text-white">
              {current ? clean(current.label) : ''}
            </div>
          </div>
          {kpi}
          {/* الجرس بألوان الترويسة الداكنة — المكوّن مشترك مع الكمبيوتر فيُعاد تشكيله هنا بمحدِّدات الأبناء وحدها؛ و«!» لأن ورقة الوضع
              الداكن (:root.dark .btn-ghost) أعلى تحديدًا فكانت تقلبه رقعةً داكنة (المراجعة الثانية 06/10/2026) */}
          {bell && (
            <span className="relative shrink-0 [&_.btn-ghost]:min-h-0 [&_.btn-ghost]:rounded-lg [&_.btn-ghost]:!border-0 [&_.btn-ghost]:!bg-white/20
              [&_.btn-ghost]:px-2.5 [&_.btn-ghost]:py-1.5 [&_.btn-ghost]:!text-white [&_.btn-ghost]:ring-1 [&_.btn-ghost]:ring-inset [&_.btn-ghost]:ring-white/30">
              {bell}
            </span>
          )}
          {onSignOut && (
            <button type="button" onClick={onSignOut}
              className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-white/20 px-2.5 py-1.5 text-[12px] font-bold text-white ring-1 ring-inset ring-white/30 backdrop-blur active:scale-95">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                strokeLinejoin="round" aria-hidden="true" className="h-[0.95rem] w-[0.95rem]">
                <path d="M15 17.5 19.5 13 15 8.5M19 13H9M11 4.5H6.5A1.5 1.5 0 0 0 5 6v14a1.5 1.5 0 0 0 1.5 1.5H11" />
              </svg>
              تسجيل الخروج
            </button>
          )}
        </div>

        {/**
          * **صفُّ البصمة والبحث** (قرار المالك: «خلّي جنب الحضور والانصراف البحثَ، ونسّق
          * ألوان الزراير دي وزرار الخروج مع ألوان الأبليكيشن»).
          *
          * وهما مكوّنان مشتركان مع الويب بألوانهما وصناديقهما، فلا يُمسّ مصدرُهما —
          * ويُعاد تشكيلُهما هنا وحدهما. وجُرّب صندوقٌ أبيضُ يضمّهما فوق التدرّج، فكان
          * رقعةً بيضاء في ترويسةٍ ملوّنة («الألوان سيئة جدًّا وحتى ضهرهم الأبيض سيء» —
          * قرار المالك 22/09/2026).
          *
          * **فلا صندوقَ ولا بياض**: ثلاثُ قطعٍ عاريةٍ على التدرّج نفسه، **بارتفاعٍ واحد
          * ‎(32px)‎ وانحناءٍ واحد** فتُقرأ صفًّا واحدًا لا ثلاثةَ أشياءَ متجاورة:
          *   · «تسجيل الحضور» — أبيضُ مصمت ونصُّه **بلون عائلة الشاشة** (`--tone`)،
          *     فهو الفعل الأوّل، ولونه من اللوجو لا لونٌ ثالثٌ يُخترع.
          *   · «تسجيل الانصراف» — أبيضُ شفّاف، فعلٌ ثانٍ لا يزاحم الأوّل.
          *   · البحث — يملأ ما بقي من العرض.
          *
          * **ولا ينضغط شيء** (قرار المالك: «لما ضغط بصمة حضور واتكتب السطر بتاع الحضور،
          * الصفحة شكلها صغر»): جملةُ التأكيد بعد البصمة تأخذ سطرها كاملًا تحت الزرّين
          * داخل صندوقها، فلا تدفع البحثَ ولا تتجاوز عرض الشاشة.
          *
          * **ومحدِّداتُ الإلباس تُكتب على ما في الصفحة لا على ما في المصدر**: الزرّان
          * صنفهما `.btn-primary`/`.btn-ghost` — و`.btn` ليس صنفًا في الصفحة أصلًا، إنّما
          * يُدمج فيهما بـ`@apply`. وجذرُ البصمة `<section>` لا `<div>`. فكانت قواعدُ
          * المقاس تُكتب على `.btn` و`div` فلا تُصيب شيئًا، بينما يُصيب لونُ النصّ الأبيض
          * زرَّ الانصراف على أرضيته البيضاء — فيختفي نصُّه (قرار المالك 22/09/2026: «بص
          * الحضور والانصراف عاملين إزاي؟»). فالمقاس الآن على `button`، والجذر على `>*`.
          *
          * **وحقلُ البحث لا ينزل عن 16px** (قرار المالك 22/09/2026: «لما أضغط على البحث
          * مش عاوزه يعمل كده — عاوز الشاشة زي ما هي»): المتصفّح على الهاتف **يكبّر الصفحة
          * كلَّها** حين تُلمس خانةُ إدخالٍ خطُّها أصغر من ذلك، فتقفز الشاشة وتخرج عن حدّها.
          * وهو ما يمنعه `index.css` أصلًا — فجاء تصغيرُ الخطّ هنا ونقضه. فالزرّان يصغران
          * (لا يُكتب فيهما) والحقلُ يبقى عند حدّه، والارتفاع واحدٌ للجميع (36px).
          */}
        {/* ورسالة رفض البصمة أو التأخير مقروءة: كانت 10px وردية باهتة على التدرّج الداكن — ومنذ رفض البصمة خارج المكتب
            (06/10/2026) هي أوّل ما يقرؤه الموظف ليعرف لماذا لم تُسجَّل بصمته، فصارت شريطًا فاتحًا بخطٍّ 13px — و«!» لأن ورقة الوضع
            الداكن تقلب لون النصّ إلى ورديٍّ فاتح فوق الشريط الفاتح نفسه (المراجعة الثالثة) */}
        {(quickTap || search) && (
          <div style={{ '--tone': TONE[familyOf(active)].on } as CSSProperties}
            className="flex flex-wrap items-center gap-1.5 px-3.5 pb-2.5
              [&_[role=search]]:w-full [&_[role=status]]:w-full
              [&_[role=status]]:text-[10px] [&_[role=status]]:font-medium [&_[role=status]]:text-white/85
              [&_[role=alert]]:w-full [&_[role=alert]]:rounded-lg [&_[role=alert]]:!bg-amber-50 [&_[role=alert]]:px-2.5 [&_[role=alert]]:py-1.5
              [&_[role=alert]]:text-[13px] [&_[role=alert]]:font-semibold [&_[role=alert]]:leading-6 [&_[role=alert]]:!text-rose-800
              [&_button]:h-9 [&_button]:min-h-0 [&_button]:gap-1.5 [&_button]:rounded-lg
              [&_button]:border-0 [&_button]:px-3 [&_button]:py-0 [&_button]:text-[12px]
              [&_button]:font-semibold [&_button]:shadow-none
              [&_.btn-primary]:!bg-white [&_.btn-primary]:!bg-none [&_.btn-primary]:!text-[color:var(--tone)]
              [&_.btn-ghost]:!bg-white/20 [&_.btn-ghost]:!text-white
              [&_.control]:h-9 [&_.control]:rounded-lg [&_.control]:border-0 [&_.control]:bg-white/20
              [&_.control]:py-0 [&_.control]:text-white [&_.control]:shadow-none
              [&_.control]:placeholder:text-white/70 [&_.text-ink-400]:text-white/70">
            {quickTap && (
              <div className="min-w-0 [&>*]:gap-1.5 [&>*]:rounded-none [&>*]:border-0 [&>*]:bg-transparent [&>*]:p-0">
                {quickTap}
              </div>
            )}
            {/**
              * **البحث سطرٌ كامل أبيض** (قرار المالك 01/10/2026: «لما ببحث في التطبيق من الموبايل في خانة البحث
              * اللي فوق خالص، مش ظاهر مربع النص اللي بكتب فيه»): كان محشورًا بجانب البصمة بعرض ~130px وأرضيةٍ
              * شفّافة على التدرّج، فيُقصّ نصّه ولا تُرى حدوده. الآن سطرٌ تحت البصمة بعرض الشاشة، أبيضُ بخطٍّ غامق
              * (16px فلا تكبر الصفحة عند لمسه)، والنتائج تحته بعرضه.
              */}
            {search && (
              <div className="w-full basis-full
                [&_.control]:!h-10 [&_.control]:!bg-white [&_.control]:!text-ink-900 [&_.control]:!text-base
                [&_.control]:placeholder:!text-ink-400 [&_.text-ink-400]:!text-ink-400
                [&_[role=listbox]]:!w-full [&_[role=listbox]]:!max-w-none">
                {search}
              </div>
            )}
          </div>
        )}
        <div aria-hidden="true" className="h-[3px]" style={{ background: accent }} />
      </header>

      {/* الشريط السفلي: أربعُ وجهاتٍ و«المزيد» — في متناول الإبهام، ثابتٌ فوق المحتوى */}
      {/**
        * ارتفاعُ القاع: `env(safe-area-inset-bottom)` تكفي حيث يُعلنها النظام (حافة
        * الآيفون)، وتساوي صفرًا في متصفّحٍ شريطُه العائم يطفو فوق الصفحة — فتُقصّ أسماءُ
        * الوجهات (قرار المالك 22/09/2026: «الزراير الرئيسية اختفت — طلّعها فوق شوية»).
        * فالمقدار حدٌّ أدنى ثابت يُضاف إليها، لا هي وحدها.
        */}
      <nav aria-label="التنقّل"
        className="fixed inset-x-0 bottom-0 z-40 border-t border-slate-200/70 bg-white/85 shadow-[0_-2px_16px_-6px_rgba(15,23,42,.18)] backdrop-blur-xl"
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)' }}>
        <div className="mx-auto flex max-w-lg items-stretch gap-0.5 px-1.5 pb-1 pt-1.5">
          {primary.map(t => <BarItem key={t.id} tab={t} on={t.id === active} dark={dark} onClick={() => pick(t.id)} />)}
          <button type="button" onClick={() => setOpen(true)} aria-expanded={open}
            aria-label={pending > 0 ? `كل الشاشات — ${pending} بانتظار المراجعة` : 'كل الشاشات'}
            className="flex flex-1 flex-col items-center gap-1 rounded-xl py-0.5 active:scale-95">
            <span className="relative flex h-8 w-[3.25rem] items-center justify-center rounded-full transition-colors duration-150"
              style={open ? { background: paint('account', dark).chip } : undefined}>
              <span aria-hidden="true" className="flex h-[1.35rem] w-[1.35rem] items-center justify-center"
                style={{ color: open ? paint('account', dark).fg : '#94a3b8' }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round"
                  className="h-full w-full"><path d="M4 7h16M4 12h16M4 17h10" /></svg>
              </span>
              {pending > 0 && <Pip />}
            </span>
            <span className="text-[11px] font-semibold" style={{ color: open ? paint('account', dark).fg : '#64748b' }}>المزيد</span>
          </button>
        </div>
      </nav>

      {open && (
        <div className="fixed inset-0 z-50 flex flex-col bg-slate-900/45 backdrop-blur-[3px]"
          onClick={() => setOpen(false)}>
          <div role="dialog" aria-label="كل الشاشات" onClick={e => e.stopPropagation()}
            className="mt-auto max-h-[88vh] overflow-y-auto rounded-t-[1.75rem] bg-white shadow-2xl"
            style={{ paddingBottom: 'calc(1.5rem + env(safe-area-inset-bottom))' }}>
            <div className="sticky top-0 z-10 rounded-t-[1.75rem] bg-white/95 px-4 pb-2 pt-2.5 backdrop-blur">
              <div aria-hidden="true" className="mx-auto mb-2.5 h-1 w-10 rounded-full bg-slate-300" />
              <div className="flex items-center justify-between">
                <h2 className="text-[15px] font-bold text-ink-900">كل الشاشات</h2>
                <button type="button" onClick={() => setOpen(false)} aria-label="إغلاق"
                  className="grid h-8 w-8 place-items-center rounded-full bg-slate-100 text-slate-500 active:scale-95">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                    aria-hidden="true" className="h-4 w-4"><path d="M6 6l12 12M18 6 6 18" /></svg>
                </button>
              </div>
            </div>

            <div className="px-4">
              {/**
                * **أدوات الترويسة مضغوطة** (قرار المالك 21/09/2026: «دول واخدين كمية كبيرة من
                * الشاشة — صغّرهم وخلّيهم متنسّقين بشكل مناسب جدًّا وprofessional»).
                *
                * والضغط هنا لا في المكوّنات نفسها: الجرس والبحث وزرّا الحضور مشتركةٌ مع
                * الويب، فلو صُغّرت في مصدرها صغُرت هناك أيضًا. فتُصغَّر **في هذا الموضع
                * وحده** بمحدِّدات الأبناء — أهمُّها `.btn` التي ارتفاعها الأدنى 48px لليد
                * على شاشة كاملة، ولا حاجة له في لوحٍ كلُّه أزرارٌ صغيرة.
                *
                * ومقياس خطّ المشروع مكبَّر درجتين (text-xs = 16px)، فيُعاد هنا إلى مقاسه
                * الطبيعي — وإلّا بدت الأداة الصغيرة بحجم عنوان.
                */}
              {children && (
                <div className="mb-3 flex flex-wrap items-center gap-1.5 rounded-2xl border border-slate-200/80 bg-slate-50/80 p-2
                  [&_.btn]:min-h-0 [&_.btn]:gap-1.5 [&_.btn]:rounded-lg [&_.btn]:px-3 [&_.btn]:py-1.5
                  [&_.btn]:text-[13px] [&_.control]:py-1.5 [&_.control]:text-[13px]
                  [&_.text-sm]:text-[13px] [&_.text-xs]:text-[11px] [&_.text-base]:text-[14px]">
                  {children}
                </div>
              )}

              <div className="grid grid-cols-2 gap-2">
                {plain.map(t => <Tile key={t.id} tab={t} on={t.id === active} dark={dark} onClick={() => pick(t.id)} />)}
              </div>

              {GROUPS.map(g => {
                const items = tabs.filter(t => t.group === g.key);
                if (items.length === 0) return null;
                const c = paint(items[0].id, dark);
                const isOpen = openGroup === g.key;
                const hasActive = items.some(t => t.id === active);
                return (
                  <section key={g.key} className="mt-3">
                    <button type="button" onClick={() => setOpenGroup(isOpen ? null : g.key)} aria-expanded={isOpen}
                      className="flex w-full items-center gap-2.5 rounded-2xl border p-2.5 text-start active:scale-[.99]"
                      style={hasActive || isOpen
                        ? { borderColor: c.fg, background: c.chip }
                        : { borderColor: IDLE_LINE(dark), background: IDLE_FACE(dark) }}>
                      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl"
                        style={{ background: c.chip, color: c.fg }}>
                        <span aria-hidden="true" className="h-[1.2rem] w-[1.2rem]">{navIcon(items[0].id)}</span>
                      </span>
                      <span className="flex-1 text-[13px] font-bold" style={{ color: c.fg }}>{g.ar}</span>
                      <span className="text-[11px] font-semibold text-slate-400">{items.length}</span>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"
                        aria-hidden="true"
                        className={`h-3.5 w-3.5 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}>
                        <path d="m6 9 6 6 6-6" />
                      </svg>
                    </button>
                    {isOpen && (
                      <div className="mt-2 grid grid-cols-2 gap-2">
                        {items.map(t => <Tile key={t.id} tab={t} on={t.id === active} dark={dark} onClick={() => pick(t.id)} />)}
                      </div>
                    )}
                  </section>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** نقطةٌ حمراء على الرمز — العددُ يُقرأ في اللوح، وهنا تكفي إشارةٌ لا رقم. */
const Pip = () => (
  <span aria-hidden="true" className="absolute end-3 top-0.5 h-2 w-2 rounded-full bg-rose-500 ring-2 ring-white" />
);

/** وجهةٌ في الشريط السفلي: قرصٌ بلون العائلة تحت الرمز حين تكون مفتوحة. */
function BarItem({ tab, on, dark, onClick }: { tab: MobileTab; on: boolean; dark: boolean; onClick: () => void }) {
  const c = paint(tab.id, dark);
  return (
    <button type="button" onClick={onClick} aria-current={on ? 'page' : undefined}
      className="flex flex-1 flex-col items-center gap-1 rounded-xl py-0.5 active:scale-95">
      <span className="relative flex h-8 w-[3.25rem] items-center justify-center rounded-full transition-colors duration-150"
        style={on ? { background: c.chip } : undefined}>
        <span aria-hidden="true" className="flex h-[1.35rem] w-[1.35rem] items-center justify-center"
          style={{ color: on ? c.fg : '#94a3b8' }}>{navIcon(tab.id)}</span>
        {tab.badge !== undefined && tab.badge > 0 && <Pip />}
      </span>
      <span className="max-w-full truncate px-0.5 text-[11px] font-semibold"
        style={{ color: on ? c.fg : '#64748b' }}>{short(tab)}</span>
    </button>
  );
}

/** بطاقةُ شاشةٍ في اللوح: رمزٌ في مربّعٍ ملوّن، والاسم كاملًا، والعدد المنتظر إن وُجد. */
function Tile({ tab, on, dark, onClick }: { tab: MobileTab; on: boolean; dark: boolean; onClick: () => void }) {
  const c = paint(tab.id, dark);
  return (
    <button type="button" onClick={onClick} aria-current={on ? 'page' : undefined}
      className="flex items-center gap-2.5 rounded-2xl border p-2.5 text-start transition-transform active:scale-[.97]"
      style={on
        ? { borderColor: c.fg, background: c.chip }
        : { borderColor: IDLE_LINE(dark), background: IDLE_FACE(dark) }}>
      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl"
        style={on ? { background: c.fg, color: dark ? '#070b12' : '#fff' } : { background: c.chip, color: c.fg }}>
        <span aria-hidden="true" className="h-[1.2rem] w-[1.2rem]">{navIcon(tab.id)}</span>
      </span>
      <span className="min-w-0 flex-1 text-[13px] font-bold leading-tight"
        style={{ color: on ? c.fg : IDLE_TEXT(dark) }}>{clean(tab.label)}</span>
      {tab.badge !== undefined && tab.badge > 0 && (
        <span className="inline-flex min-w-[1.25rem] shrink-0 items-center justify-center rounded-full bg-rose-500 px-1 py-0.5 text-[10px] font-bold leading-none text-white">
          {tab.badge}
        </span>
      )}
    </button>
  );
}
